import type {
  BffLogicalDeviceResourceReadApi,
  BffPhysicalDeviceResourceReadApi,
} from '@ingenico/api-sdk-provisioning-private';
import type { CustomDeviceFamily } from '@ingenico/react-section-utils';
import type { DeviceName } from '@ingenico/react-ui';
import { isEqual, isNumber, toString } from 'lodash-es';
import type { DeviceConnectionStatus, DeviceMetrics } from '../../../domain/models';
import { DeviceConnectionStatusType } from '../../../domain/models';
import type { DeviceInfo, NonRequiredDeviceConnectionStatus } from '../../../domain/models/deviceInfo.model';
import { dashString } from '../../../utils/dashString.util';
import type {
  LogicalDeviceSummaryCardConnectivity,
  LogicalDeviceSummaryCardProps,
  LogicalDeviceSummaryHardware,
} from '../../logical-device-summary-card/logicalDeviceSummaryCard.model';
import { LogicalDeviceConnectivityStatus } from '../../logical-device-summary-card/logicalDeviceSummaryCard.model';
import { deviceConfigurationDisplayedInfo } from '../../logical-device-terminal-details-card/logicalDeviceTerminalDetailsCard.util';
import type { UseLogicalDeviceInfoAttachedVmReturn } from './LogicalDeviceInfo.model';

const regexNotAcceptSpecialChars = new RegExp(/[^\s\w]/g);

export function getDeviceModel(deviceName: string): DeviceName {
  const removeSpecialChars = deviceName.replaceAll(regexNotAcceptSpecialChars, '');
  return removeSpecialChars.toLowerCase() as DeviceName;
}

function getDeviceFamily(deviceModel?: string | null, deviceFamilyQuery?: CustomDeviceFamily[] | null): string {
  if (!deviceModel || !deviceFamilyQuery) {
    return '';
  }

  const matchingFamily = deviceFamilyQuery.find((family) =>
    family.deviceModels?.some((model) => model.name === deviceModel),
  );

  return matchingFamily?.name ?? '';
}

export function getSummaryGeneralData(
  logicalDevice?: BffLogicalDeviceResourceReadApi | null,
  deviceMetrics?: DeviceMetrics | null,
  configurationStatus?: DeviceConnectionStatus | NonRequiredDeviceConnectionStatus | null,
  deviceFamilyQuery?: CustomDeviceFamily[] | null,
  deviceInfo?: Pick<DeviceInfo, 'screenLockingStatus'> | null,
): LogicalDeviceSummaryCardProps['general'] {
  const { attachedPhysicalDevices = [] } = logicalDevice ?? {};
  const [physicalDevices] = attachedPhysicalDevices;
  const deviceName = toString(physicalDevices?.model);
  return {
    name: deviceName,
    model: getDeviceModel(deviceName),
    manufacturer: toString(physicalDevices?.manufacturer),
    serialNumber: toString(physicalDevices?.serialNumber),
    partNumber: toString(physicalDevices?.partNumber),
    isConnected: configurationStatus?.isConnected,
    connectivityStatus:
      configurationStatus?.connectionStatus === DeviceConnectionStatusType.Connected
        ? LogicalDeviceConnectivityStatus.Online
        : LogicalDeviceConnectivityStatus.Offline,
    lastSyncDate: deviceMetrics?.lastSyncDate,
    family: getDeviceFamily(physicalDevices?.model, deviceFamilyQuery),
    storeName: logicalDevice?.hierarchyMetadata.storeName,
    reference: toString(logicalDevice?.reference),
    isScreenLocked: deviceMetrics?.isScreenLocked,
    screenLockingStatus: deviceInfo?.screenLockingStatus,
  };
}

function buildConnectivityInfo(
  connectivityKey: keyof LogicalDeviceSummaryCardConnectivity,
  deviceMetrics?: DeviceMetrics | null,
  nameKey?: keyof DeviceMetrics,
  statusKey?: keyof DeviceMetrics,
  tooltipKey?: keyof DeviceMetrics,
  strength?: keyof DeviceMetrics,
  type?: string,
): LogicalDeviceSummaryCardProps['connectivity'] {
  return deviceMetrics && nameKey && statusKey
    ? {
        [connectivityKey]: {
          name: deviceMetrics?.[nameKey],
          status: deviceMetrics?.[statusKey] === 'active' ? 'active' : 'inactive',
          tooltip: tooltipKey ? deviceMetrics[tooltipKey] : undefined,
          strength: strength ? deviceMetrics?.[strength] : undefined,
          type,
        },
      }
    : {};
}

export function getDeviceConnectivityData(
  deviceMetrics?: DeviceMetrics | null,
): LogicalDeviceSummaryCardProps['connectivity'] {
  const SSIDData = buildConnectivityInfo('SSID', deviceMetrics, 'SSID', 'statusLAN');
  const settingsData = buildConnectivityInfo('bluetooth', deviceMetrics, 'bluetooth', 'bluetoothStatus');
  const mobileNetworkData = buildConnectivityInfo(
    'mobileNetwork',
    deviceMetrics,
    deviceMetrics?.mobileNetworkType ? 'mobileNetworkName' : 'mobileNetworkType',
    'mobileNetworkStatus',
    'mobileNetworkName',
    'mobileNetworkStrength',
    deviceMetrics?.mobileNetworkType ?? undefined,
  );
  const SIMData = buildConnectivityInfo('SIM', deviceMetrics, 'SIMCardName', 'SIMCardStatus');
  const ethernetData = buildConnectivityInfo('ethernet', deviceMetrics, 'ethernet', 'ethernetStatus');
  return {
    ...SSIDData,
    ...settingsData,
    ...mobileNetworkData,
    ...SIMData,
    ...ethernetData,
  };
}

function buildDeviceHardwareData(
  hardwareKey: keyof LogicalDeviceSummaryHardware,
  deviceMetrics?: DeviceMetrics | null,
  chartKey?: keyof DeviceMetrics,
  isInverse?: boolean,
): LogicalDeviceSummaryCardProps['hardware'] {
  return deviceMetrics && chartKey && deviceMetrics?.[chartKey]
    ? {
        [hardwareKey]: {
          value: isNumber(deviceMetrics?.[chartKey]) ? deviceMetrics?.[chartKey] : 0,
          inverse: !!isInverse,
        },
      }
    : {};
}

export function getDeviceHardwareCharts(
  deviceMetrics?: DeviceMetrics | null,
): LogicalDeviceSummaryCardProps['hardware'] {
  const batteryData = buildDeviceHardwareData('battery', deviceMetrics, 'batteryUsage');
  const CPUData = buildDeviceHardwareData('CPU', deviceMetrics, 'CPUUsage');
  const RAMData = buildDeviceHardwareData('RAM', deviceMetrics, 'RAMUsage');
  const flashData = buildDeviceHardwareData('flash', deviceMetrics, 'flashUsage', true);
  return {
    ...batteryData,
    ...CPUData,
    ...RAMData,
    ...flashData,
  };
}

export function checkShouldShowLogicalDevice(deviceMetrics?: DeviceMetrics | null): boolean {
  return !!deviceMetrics?.dateOfReport || !!deviceMetrics?.ipAddress || !!deviceMetrics?.lastReportLocation;
}

export function checkShouldShowTerminalDetails(
  deviceMetrics?: DeviceMetrics | null,
  physicalDevice?: BffPhysicalDeviceResourceReadApi,
): boolean {
  const displayedData = Object.keys(deviceConfigurationDisplayedInfo);
  return deviceMetrics || physicalDevice
    ? displayedData.reduce((accum, current) => {
        return accum
          ? true
          : !!deviceMetrics?.[current as keyof DeviceMetrics] ||
              !!physicalDevice?.[current as keyof BffPhysicalDeviceResourceReadApi];
      }, false)
    : false;
}

export function getDeviceSIMInformation(
  deviceMetrics?: DeviceMetrics | null,
): UseLogicalDeviceInfoAttachedVmReturn['deviceSIMInformation'] {
  return {
    networkOperator: dashString(deviceMetrics?.mobileNetworkName),
    networkStatus: isEqual(deviceMetrics?.mobileNetworkStatus, 'active') ? 'ACTIVE' : 'INACTIVE',
    IMSI: dashString(deviceMetrics?.IMSI),
    IMEI: dashString(deviceMetrics?.IMEI),
    operatorSIM: dashString(deviceMetrics?.SIMCardName),
  };
}
export function getDeviceBatteryInformation(
  deviceMetrics?: DeviceMetrics | null,
): UseLogicalDeviceInfoAttachedVmReturn['deviceBatteryInformation'] {
  return {
    cycles: dashString(deviceMetrics?.batteryCycles),
    health: dashString(deviceMetrics?.batteryHealth),
  };
}
